import { getFreshToken } from "@/apis/auth/auth";

/**
 * Configuración base del cliente HTTP
 * @constant {string} API_BASE_URL - La URL base de la API
 */
const API_BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) || "";

/**
 * Tipos para las opciones de fetch
 * @interface FetchOptions - Opciones de fetch
 * @extends {RequestInit} - Extiende de RequestInit
 * @property {Record<string, string | number | boolean | undefined>} params - Parámetros de la URL
 */
interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * Tipos para las opciones de fetch con blob
 * @interface BlobFetchOptions - Opciones de fetch para blobs
 * @extends {FetchOptions} - Extiende de FetchOptions
 * @property {boolean} returnBlob - Si debe retornar un Blob en lugar de JSON
 * @property {string} filename - Nombre del archivo para descargas
 */
interface BlobFetchOptions extends FetchOptions {
  returnBlob?: boolean;
  filename?: string;
}

/**
 * Clase para manejar errores de API
 * @class ApiError - Clase para manejar errores de API
 * @extends {Error} - Extiende de Error
 * @property {number} status - El código de estado de la respuesta
 * @property {string} message - El mensaje de error
 * @property {string} code - El código de error
 * @property {Record<string, unknown>} details - Los detalles del error
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public code?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Función para construir la URL con parámetros
 * @param endpoint - La URL del endpoint
 * @param params - Los parámetros de la URL
 * @returns {string} La URL construida
 */
function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const url = new URL(endpoint, API_BASE_URL);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Función para manejar la respuesta
 * @param response - La respuesta de la petición
 * @returns {Promise<T>} La respuesta de la petición
 */
async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type");
  const isJson = contentType?.includes("application/json");
  const data = isJson ? await response.json() : null;

  if (!response.ok || (data && typeof data === "object" && data.success === false)) {
    const errorData = data || {
      message: response.statusText || "Error desconocido",
      code: `HTTP_${response.status}`,
      success: false,
    };

    throw new ApiError(
      response.status,
      errorData.message || errorData.error || "Error desconocido",
      errorData.code,
      errorData.details
    );
  }

  return data as T;
}

/**
 * Función para manejar respuestas de blob
 * @param response - La respuesta de la petición
 * @returns {Promise<Blob>} El blob de la respuesta
 */
async function handleBlobResponse(response: Response): Promise<Blob> {
  if (!response.ok) {
    let errorData: any;

    try {
      errorData = await response.json();
    } catch {
      errorData = {
        message: response.statusText || "Error desconocido",
        code: `HTTP_${response.status}`,
        success: false,
      };
    }

    throw new ApiError(
      response.status,
      errorData.message || errorData.error || "Error desconocido",
      errorData.code,
      errorData.details
    );
  }

  return response.blob();
}

/**
 * Función para descargar un archivo desde un blob
 * @param blob - El blob a descargar
 * @param filename - El nombre del archivo
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Extrae el nombre de archivo de Content-Disposition (ASCII o RFC 5987 filename*).
 */
export function parseContentDispositionFilename(
  header: string | null
): string | undefined {
  if (!header) return undefined;
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].replace(/\+/g, "%20"));
    } catch {
      return utf8[1];
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1];
  const plain = /filename=([^;\s]+)/i.exec(header);
  if (plain) return plain[1].replace(/^"|"$/g, "");
  return undefined;
}

export interface PostJsonForBlobOptions {
  /**
   * Si true, no envía Authorization (p. ej. POST con `installLinkId` validado en servidor).
   */
  skipAuthorization?: boolean;
  /**
   * Timeout en milisegundos para abortar la petición.
   * Si no se define, la petición espera hasta responder.
   */
  timeoutMs?: number;
}

/**
 * POST JSON y respuesta binaria (p. ej. application/zip). No usa handleResponse JSON.
 */
export async function postJsonForBlob(
  endpoint: string,
  data: unknown,
  options: PostJsonForBlobOptions = {}
): Promise<{ blob: Blob; filename?: string }> {
  const url = buildUrl(endpoint, undefined);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/zip, application/octet-stream, */*",
  };
  if (!options.skipAuthorization) {
    const token = localStorage.getItem("authToken");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const controller = new AbortController();
  const config: RequestInit = {
    method: "POST",
    headers,
    body: JSON.stringify(data),
    signal: controller.signal,
  };

  const timeoutMs = options.timeoutMs;
  const timeoutId =
    typeof timeoutMs === "number" && timeoutMs > 0
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : undefined;

  try {
    const response = options.skipAuthorization
      ? await fetch(url, config)
      : await makeRequest(url, config);

    const filename = parseContentDispositionFilename(
      response.headers.get("Content-Disposition")
    );
    const blob = await handleBlobResponse(response);
    return { blob, filename };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError(
        0,
        "La descarga excedió el tiempo de espera. Intenta nuevamente.",
        "TIMEOUT_ERROR"
      );
    }
    throw error;
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}

/**
 * Función para hacer una petición HTTP con manejo de token
 * @param url - La URL de la petición
 * @param config - La configuración de la petición
 * @param retryOn401 - Si debe reintentar en caso de error 401
 * @returns {Promise<Response>} La respuesta de la petición
 */
async function makeRequest(
  url: string,
  config: RequestInit,
  retryOn401: boolean = true
): Promise<Response> {
  const response = await fetch(url, config);

  // Si es un error 401 y tenemos token, intentar refrescar el token
  if (response.status === 401 && retryOn401 && localStorage.getItem("authToken")) {
    try {
      const freshToken = await getFreshToken(true);

      if (freshToken) {
        // Actualizar el token en localStorage
        localStorage.setItem("authToken", freshToken);

        // Actualizar el header de autorización
        const updatedConfig = {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${freshToken}`,
          },
        };

        // Reintentar la petición con el token fresco
        return await fetch(url, updatedConfig);
      }
    } catch (tokenError) {
      console.error("Error refrescando token:", tokenError);
      // Si falla el refresh del token, limpiar localStorage
      localStorage.removeItem("authToken");
    }
  }

  return response;
}

/**
 * Función principal para hacer requests HTTP
 * @param endpoint - La URL del endpoint
 * @param options - Las opciones de la petición
 * @returns {Promise<T>} La respuesta de la petición
 */
export async function httpClient<T = unknown>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;

  // Construir URL con parámetros
  const url = buildUrl(endpoint, params);

  // Configuración por defecto
  const config: RequestInit = {
    headers: {
      ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...fetchOptions.headers,
    },
    ...fetchOptions,
  };

  // Agregar token de autenticación si existe
  const token = localStorage.getItem("authToken");

  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  try {
    const response = await makeRequest(url, config);
    return await handleResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Error de red o desconocido
    throw new ApiError(
      0,
      error instanceof Error ? error.message : "Error de conexión",
      "NETWORK_ERROR"
    );
  }
}

/**
 * Función para hacer requests HTTP que retornan blobs
 * @param endpoint - La URL del endpoint
 * @param options - Las opciones de la petición
 * @returns {Promise<Blob>} El blob de la respuesta
 */
export async function httpClientBlob(
  endpoint: string,
  options: BlobFetchOptions = {}
): Promise<Blob> {
  const { params, ...fetchOptions } = options;

  // Construir URL con parámetros
  const url = buildUrl(endpoint, params);

  // Configuración por defecto - NO incluir Content-Type para blobs
  const config: RequestInit = {
    headers: {
      ...fetchOptions.headers,
    },
    ...fetchOptions,
  };

  // Agregar token de autenticación si existe
  const token = localStorage.getItem("authToken");

  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  try {
    const response = await makeRequest(url, config);
    return await handleBlobResponse(response);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Error de red o desconocido
    throw new ApiError(
      0,
      error instanceof Error ? error.message : "Error de conexión",
      "NETWORK_ERROR"
    );
  }
}

/**
 * Función específica para descargas que no incluye headers innecesarios
 * @param endpoint - La URL del endpoint
 * @param options - Las opciones de la petición
 * @returns {Promise<Blob>} El blob de la respuesta
 */
export async function downloadClient(
  endpoint: string,
  options: BlobFetchOptions = {}
): Promise<Blob> {
  const { params, ...fetchOptions } = options;

  // Construir URL con parámetros
  const url = buildUrl(endpoint, params);

  // Configuración mínima - solo headers explícitos del usuario
  const config: RequestInit = {
    headers: {
      ...fetchOptions.headers,
    },
    ...fetchOptions,
  };

  // Solo agregar Authorization si existe token y no hay headers explícitos
  const token = localStorage.getItem("authToken");

  if (token && !fetchOptions.headers) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  try {
    const response = await makeRequest(url, config);
    return await handleBlobResponse(response);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Error de red o desconocido
    throw new ApiError(
      0,
      error instanceof Error ? error.message : "Error de conexión",
      "NETWORK_ERROR"
    );
  }
}

/**
 * Función para descargar un archivo directamente desde una URL
 * @param endpoint - La URL del endpoint
 * @param filename - El nombre del archivo
 * @param options - Las opciones de la petición
 * @returns {Promise<void>}
 */
export async function downloadFile(
  endpoint: string,
  filename: string,
  options: BlobFetchOptions = {}
): Promise<void> {
  try {
    const blob = await downloadClient(endpoint, {
      ...options,
      returnBlob: true,
    });
    downloadBlob(blob, filename);
  } catch (error) {
    throw new ApiError(
      0,
      error instanceof Error ? error.message : "Error al descargar el archivo",
      "DOWNLOAD_ERROR"
    );
  }
}

/**
 * Función para subir un archivo
 * @param endpoint - La URL del endpoint
 * @param file - El archivo a subir
 * @param options - Las opciones adicionales
 * @returns {Promise<T>} La respuesta de la petición
 */
export async function uploadFile<T = unknown>(
  endpoint: string,
  file: File,
  options: FetchOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;

  // Construir URL con parámetros
  const url = buildUrl(endpoint, params);

  // Crear FormData
  const formData = new FormData();
  formData.append("file", file);

  // Configuración por defecto
  const config: RequestInit = {
    method: "POST",
    body: formData,
    headers: {
      ...fetchOptions.headers,
    },
    ...fetchOptions,
  };

  // Agregar token de autenticación si existe
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  try {
    const response = await makeRequest(url, config);
    return await handleResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Error de red o desconocido
    throw new ApiError(
      0,
      error instanceof Error ? error.message : "Error de conexión",
      "NETWORK_ERROR"
    );
  }
}

/**
 * Métodos HTTP específicos
 * @constant {Object} http - Métodos HTTP específicos
 * @property {<T = unknown>(endpoint: string, params?: Record<string, string | number | boolean | undefined>) => Promise<T>} get - Método GET
 * @property {<T = unknown>(endpoint: string, data?: unknown) => Promise<T>} post - Método POST
 * @property {<T = unknown>(endpoint: string, data?: unknown) => Promise<T>} put - Método PUT
 * @property {<T = unknown>(endpoint: string, data?: unknown) => Promise<T>} patch - Método PATCH
 * @property {<T = unknown>(endpoint: string) => Promise<T>} delete - Método DELETE
 */
export const http = {
  get: <T = unknown>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ) => httpClient<T>(endpoint, { method: "GET", params }),

  post: <T = unknown>(endpoint: string, data?: unknown) =>
    httpClient<T>(endpoint, {
      method: "POST",
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    }),

  put: <T = unknown>(endpoint: string, data?: unknown) =>
    httpClient<T>(endpoint, {
      method: "PUT",
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    }),

  patch: <T = unknown>(endpoint: string, data?: unknown) =>
    httpClient<T>(endpoint, {
      method: "PATCH",
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    }),

  delete: <T = unknown>(endpoint: string) =>
    httpClient<T>(endpoint, { method: "DELETE" }),

  // Métodos específicos para blobs y archivos
  getBlob: (
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ) => httpClientBlob(endpoint, { method: "GET", params }),

  download: (
    endpoint: string,
    filename: string,
    params?: Record<string, string | number | boolean | undefined>
  ) => downloadFile(endpoint, filename, { method: "GET", params }),

  downloadClient: (
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ) => downloadClient(endpoint, { method: "GET", params }),

  upload: <T = unknown>(
    endpoint: string,
    file: File,
    params?: Record<string, string | number | boolean | undefined>
  ) => uploadFile<T>(endpoint, file, { method: "POST", params }),
};
